import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, Link, Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { api, ApiError, isNetworkError } from "./api";
import { useReconnect, useAppViewportHeight } from "./hooks";
import { Loading, StatusDot, WarnIcon, Wordmark } from "./components";
import { TOAST_EVENT } from "./grantwarn";
import { loginTarget, safeNext } from "./login";
import { ALERTS_CHANGED_EVENT } from "./alerts";
import { clearChunkReload, retryChunk } from "./chunkreload";
import { AppHeaderContext } from "./appHeader";
import Dashboard from "./pages/Dashboard";
import SharedApps from "./pages/SharedApps";
import AppDetail from "./pages/AppDetail";
import Chats from "./pages/Chats";
import Settings from "./pages/Settings";
import WelcomeModal from "./pages/WelcomeModal";
import Connections from "./pages/Connections";
import Explore from "./pages/Explore";
import Admin from "./pages/Admin";
import Alerts from "./pages/Alerts";
import Docs from "./pages/Docs";

// The popped-out, full-window terminal (also reachable directly). xterm is heavy,
// so it stays a lazy chunk, loaded only when a terminal is actually opened.
const AppTerminal = lazy(retryChunk(() => import("./pages/AppTerminal")));

const logout = async () => {
  try {
    await fetch("/auth/logout", { method: "POST", credentials: "same-origin" });
  } finally {
    window.location.reload();
  }
};

// A small dropdown hook: closes on an outside click or Escape.
const useNavDropdown = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return { open, setOpen, ref };
};

// The "Apps" nav item: clicking goes to the app list, hovering opens a switcher
// that lists the owner's apps so you can jump straight to any one (handy from an
// app detail page). The list is fetched the first time it opens.
const AppsMenu = ({ canCreate = true }) => {
  const { open, setOpen, ref } = useNavDropdown();
  const [apps, setApps] = useState(null); // null = not loaded yet
  const [failed, setFailed] = useState(false);
  const closeTimer = useRef(null);
  const load = useCallback(() => {
    setFailed(false);
    api
      .get("/api/apps")
      // Conversations live on their own Chats page, not in the Apps dropdown.
      .then((all) => setApps(all.filter((a) => a.kind !== "chat")))
      .catch(() => setFailed(true));
  }, []);
  useEffect(() => {
    // Prefetch on mount so the switcher is populated the moment it opens, rather
    // than showing "Loading..." on the first hover.
    load();
  }, [load]);
  // Hover opens it (a small close delay bridges the gap to the popup); clicking
  // "Apps" still navigates to the list. Reload on every open so an app created
  // since the last fetch shows up without a full page refresh.
  const openNow = () => {
    clearTimeout(closeTimer.current);
    load();
    setOpen(true);
  };
  const closeSoon = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  };
  return (
    <div className="nav-apps" ref={ref} onPointerEnter={(e) => e.pointerType === "mouse" && openNow()} onPointerLeave={(e) => e.pointerType === "mouse" && closeSoon()}>
      <NavLink to="/" end className="nav-apps-link" onClick={() => setOpen(false)}>
        Apps
      </NavLink>
      {open && (
        <div className="nav-apps-pop" role="menu">
          {apps === null && !failed && <div className="nav-apps-note">Loading...</div>}
          {failed && <div className="nav-apps-note">Couldn't load apps</div>}
          {apps &&
            /* Archived apps are put away, not worked on: the switcher is for
               jumping between live work, so they stay off it (the dashboard's
               "Archived" toggle is where they live). */
            apps.filter((a) => !a.archived).map((a) => (
              <Link
                key={a.name}
                to={`/app/${a.name}`}
                role="menuitem"
                className="nav-apps-item"
                onClick={() => setOpen(false)}
              >
                <StatusDot running={a.running} appRunning={a.app_running} />
                <span>{a.name}</span>
              </Link>
            ))}
          {canCreate && (
            <>
              {apps && apps.filter((a) => !a.archived).length > 0 && <div className="nav-apps-div" />}
              <Link to="/?new=1" role="menuitem" className="nav-apps-all nav-apps-new" onClick={() => setOpen(false)}>
                + New app
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// The sign-in page offers what the instance has: Google, a password form, or
// both. It asks the server first, because nobody is signed in yet and the
// account payload (which carries every other capability flag) needs a session.
// A ?next= from a private app's redirect is honoured after a password login;
// the Google button carries it along as a query parameter.
const Login = () => {
  const [methods, setMethods] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const next = new URLSearchParams(window.location.search).get("next") || "";
  useEffect(() => {
    fetch("/auth/methods", { credentials: "same-origin" })
      .then((r) => r.json())
      .then(setMethods)
      .catch(() => setMethods({ google: true, password: false }));
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/password", { email: email.trim(), password });
      // Only a same-host path is followed; anything else lands on the dashboard
      const here = window.location.pathname + window.location.search + window.location.hash;
      window.location.href = loginTarget(next, here, window.location.origin);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  const googleHref = "/auth/google" + (next ? "?next=" + encodeURIComponent(safeNext(next, window.location.origin)) : "");
  return (
    <div className="center-page">
      <div className="card gate-card">
        <Wordmark big />
        <p className="gate-pitch">
          Self-hosted mini-apps: each one gets its own container, SSH access, a subdomain and automatic TLS.
        </p>
        {methods === null && <p className="loading">Loading...</p>}
        {methods && methods.password && (
          <form className="login-form" onSubmit={submit}>
            {error && (
              <div className="banner banner-error" role="alert">
                <span>{error}</span>
              </div>
            )}
            <input type="email" id="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" aria-label="Email" autoComplete="username" required disabled={busy} />
            <input type="password" id="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" aria-label="Password" autoComplete="current-password" required disabled={busy} />
            <button type="submit" className="btn btn-primary btn-wide" disabled={busy || !email || !password}>
              {busy ? "Signing in..." : "Sign in"}
            </button>
          </form>
        )}
        {methods && methods.google && methods.password && (
          <div className="login-or" aria-hidden="true">
            <span>or</span>
          </div>
        )}
        {methods && methods.google && (
          <a className="btn btn-primary btn-wide login-google" href={googleHref}>
            Sign in with Google
          </a>
        )}
        {methods && !methods.google && !methods.password && (
          <p className="hint">Web sign-in is not configured on this server.</p>
        )}
      </div>
    </div>
  );
};

const Pending = ({ account }) => (
  <div className="center-page">
    <div className="card gate-card">
      <Wordmark big />
      <h1>Waiting for approval</h1>
      <p className="gate-pitch">
        Your account <strong>{account.email}</strong> has been created, but an admin needs to approve it before you can create
        apps. Check back later.
      </p>
      <button type="button" className="btn" onClick={logout}>
        Log out
      </button>
    </div>
  </div>
);

const Denied = ({ account }) => (
  <div className="center-page">
    <div className="card gate-card">
      <Wordmark big />
      <h1>Access denied</h1>
      <p className="gate-pitch">
        Access for <strong>{account.email}</strong> has been denied by an admin. If you think this is a mistake, contact the
        person running this hostit instance.
      </p>
      <button type="button" className="btn" onClick={logout}>
        Log out
      </button>
    </div>
  </div>
);

const LoadFailed = ({ message, onRetry }) => (
  <div className="center-page">
    <div className="card gate-card">
      <Wordmark big />
      <h1>Something went wrong</h1>
      <p className="gate-pitch">{message}</p>
      <button type="button" className="btn" onClick={onRetry}>
        Try again
      </button>
    </div>
  </div>
);

// The dashboard, profile and admin views are tables and grids, and 920px
// squeezes them for no reason on a normal screen. The docs match this width
// through their own .docs-container (they render their own <main>); the app
// page is the one exception, staying full-bleed.
const roomyPath = (pathname) =>
  pathname === "/" ||
  pathname === "/settings" ||
  pathname === "/connections" ||
  pathname === "/admin" ||
  pathname === "/alerts" ||
  pathname === "/explore" ||
  pathname === "/chats" ||
  pathname.startsWith("/chats/");

// RoutedMain is <main> with the width the current route wants. It exists as its
// own component because App renders the Router, so only a child of it can read
// the location.
const RoutedMain = ({ children }) => {
  const { pathname } = useLocation();
  return <main className={"container" + (roomyPath(pathname) ? " container-roomy" : "")}>{children}</main>;
};

const BellIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 3a4 4 0 0 0-4 4c0 4-1.5 5-2 6h12c-.5-1-2-2-2-6a4 4 0 0 0-4-4z" />
    <path d="M8.5 16a1.5 1.5 0 0 0 3 0" />
  </svg>
);

// The bell counts open, undismissed alerts (the admins' system ones and the
// viewer's own) and leads to the Alerts page. A connection that needs
// reconnecting is itself an alert, so it is in the count, not beside it. The
// count polls on its own timer: it changes while a page sits open, which the
// account payload never did.
const alertsPollMs = 30000;

const AlertBell = () => {
  const [count, setCount] = useState(0);
  const load = useCallback(() => {
    api
      .get("/api/alerts/summary")
      .then((sum) => setCount((sum.system || 0) + (sum.mine || 0)))
      .catch(() => {}); // a blip must not blank the bell; the next poll heals it
  }, []);
  useEffect(() => {
    load();
    const timer = setInterval(load, alertsPollMs);
    // A dismiss or undo on the Alerts page recounts now, not at the next poll
    window.addEventListener(ALERTS_CHANGED_EVENT, load);
    return () => {
      clearInterval(timer);
      window.removeEventListener(ALERTS_CHANGED_EVENT, load);
    };
  }, [load]);
  useReconnect(load);
  const label = count === 0 ? "Alerts" : `${count} open alert${count === 1 ? "" : "s"}`;
  return (
    <Link to="/alerts" className={"nav-bell" + (count === 0 ? " nav-bell-quiet" : "")} title={label} aria-label={label}>
      <BellIcon />
      {count > 0 && <span className="nav-bell-count">{count}</span>}
    </Link>
  );
};

const Nav = ({ account, appHeader }) => {
  // The app detail page runs full width; the nav widens to match, so the logo and
  // links slide out to the left edge and the avatar to the right.
  const { pathname } = useLocation();
  // The app detail page and the Chats page both run full width, so the nav
  // widens to match: the logo slides to the left edge and the avatar to the right.
  const wide = /^\/app\/[^/]+(\/[^/]+)?$/.test(pathname) || pathname.startsWith("/chats");
  // Full-width (wide) beats the 1240px roomy cap: on the chats page both would
  // match, and roomy would otherwise win the nav's max-width and keep it narrow.
  const roomy = !wide && roomyPath(pathname);
  // On phones the app's back+name replaces the logo, so there is a single top bar.
  const onApp = wide && appHeader;
  return (
    <header className={"nav" + (wide ? " nav-wide" : "") + (roomy ? " nav-roomy" : "") + (onApp ? " nav-hasappid" : "")}>
      <div className="nav-inner">
      <Link to="/" className="nav-brand">
        <Wordmark />
      </Link>
      {onApp && (
        <div className="nav-appid">
          <Link to="/" className="nav-appid-back" aria-label="Back to apps" title="Back to apps">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9.5 3.5 5 8l4.5 4.5" />
            </svg>
          </Link>
          <span className="nav-appid-name">{appHeader.name}</span>
          <StatusDot running={appHeader.running} appRunning={appHeader.appRunning} appState={appHeader.appState} pending={appHeader.pending} archived={appHeader.archived} />
        </div>
      )}
      {/* Inline on wide screens; on narrow ones these move into the profile menu,
          so there is a single menu (the avatar), not also a burger. */}
      <div className="nav-menu">
        <nav className="nav-links">
          <AppsMenu canCreate={account.role !== "viewer"} />
          {account.assistant_enabled && account.role !== "viewer" && <NavLink to="/chats">Chats</NavLink>}
          {account.app_listing_enabled && <NavLink to="/explore">Explore</NavLink>}
          <NavLink to="/connections">Connections</NavLink>
          {account.role === "admin" && <NavLink to="/admin">Admin</NavLink>}
          {/* A reference you read alongside the app, so: its own tab */}
          <a href="/docs/user" target="_blank" rel="noreferrer">
            Docs
          </a>
        </nav>
      </div>
      <div className="nav-right">
        <AlertBell />
        <ProfileMenu account={account} />
      </div>
      </div>
    </header>
  );
};

// The account, behind a circular initial avatar in the top-right corner: it shows
// the name Google gave us (falling back to the email), and is the single menu on
// narrow screens -- the nav links live inside it there, so there is no burger.
// Menu icons, lucide-style, 16px, drawn inline like the rest of the app.
const MenuIcon = ({ name }) => {
  const paths = {
    apps: <><rect x="2.5" y="2.5" width="4.5" height="4.5" rx="1" /><rect x="9" y="2.5" width="4.5" height="4.5" rx="1" /><rect x="2.5" y="9" width="4.5" height="4.5" rx="1" /><rect x="9" y="9" width="4.5" height="4.5" rx="1" /></>,
    chats: <path d="M13.5 10.5a1 1 0 0 1-1 1H6l-3.5 2.5v-10a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v6.5z" />,
    explore: <><circle cx="8" cy="8" r="6" /><path d="m10.5 5.5-1.5 4-4 1.5 1.5-4z" /></>,
    connections: <><path d="M6.5 9.5 9.5 6.5" /><path d="M7 4.5 8.5 3a2.5 2.5 0 0 1 3.5 3.5L10.5 8" /><path d="M9 11.5 7.5 13A2.5 2.5 0 0 1 4 9.5L5.5 8" /></>,
    admin: <path d="M8 2 3 4v4c0 3 2.2 5 5 6 2.8-1 5-3 5-6V4z" />,
    docs: <><path d="M8 4.5c-1-1-2.5-1.5-5-1.5v9c2.5 0 4 .5 5 1.5 1-1 2.5-1.5 5-1.5v-9c-2.5 0-4 .5-5 1.5z" /><path d="M8 4.5v9" /></>,
    tour: <><path d="M8 2.5v2M8 11.5v2M2.5 8h2M11.5 8h2" /><path d="M8 5.5 9 7.2 11 8 9 8.8 8 10.5 7 8.8 5 8z" /></>,
    about: <><circle cx="8" cy="8" r="6" /><path d="M8 7.2v3.8M8 5h.01" /></>,
    settings: (
      <g transform="scale(0.6667)" strokeWidth="2.25">
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
        <circle cx="12" cy="12" r="3" />
      </g>
    ),
    logout: <><path d="M6 13.5H3.5a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1H6" /><path d="M10.5 11 13.5 8l-3-3" /><path d="M13.5 8H6.5" /></>,
  };
  return (
    <svg className="nav-menu-icon" viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
};

const ProfileMenu = ({ account }) => {
  const [open, setOpen] = useState(false);
  const [about, setAbout] = useState(false);
  const ref = useRef(null);
  const closeTimer = useRef(null);
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const close = () => setOpen(false);
  // Hover opens it (a small close delay bridges the gap to the popup), matching the
  // Apps menu; clicking the avatar still toggles it.
  const openNow = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  };
  const name = (account.name || "").trim();
  const initial = (name || account.email || "?").charAt(0).toUpperCase();
  return (
    <div className="nav-profile" ref={ref} onPointerEnter={(e) => e.pointerType === "mouse" && openNow()} onPointerLeave={(e) => e.pointerType === "mouse" && closeSoon()}>
      <button
        type="button"
        className="avatar"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account"
        title={name || account.email}
      >
        {initial}
      </button>
      {open && (
        <div className="nav-profile-pop" role="menu">
          <div className="nav-profile-head">
            <span className="avatar avatar-lg" aria-hidden="true">
              {initial}
            </span>
            <span className="nav-profile-who">
              {name && <span className="nav-profile-name">{name}</span>}
              <span className="nav-profile-email" title={account.email}>
                {account.email}
              </span>
            </span>
          </div>
          <div className="nav-profile-div" />
          {/* Every page, grouped: the places, the account, the help, the exit. On
              narrow screens this is the only menu; on wide ones the bar repeats
              the first groups inline. The welcome page is offered only when the
              operator configured one (welcome-url). */}
          <div className="nav-profile-nav">
            <NavLink to="/" end role="menuitem" onClick={close}>
              <MenuIcon name="apps" />
              Apps
            </NavLink>
            {account.assistant_enabled && account.role !== "viewer" && (
              <NavLink to="/chats" role="menuitem" onClick={close}>
                <MenuIcon name="chats" />
                Chats
              </NavLink>
            )}
            {account.app_listing_enabled && (
              <NavLink to="/explore" role="menuitem" onClick={close}>
                <MenuIcon name="explore" />
                Explore
              </NavLink>
            )}
          </div>
          <div className="nav-profile-div" />
          <div className="nav-profile-nav">
            <NavLink to="/connections" role="menuitem" onClick={close}>
              <MenuIcon name="connections" />
              Connections
            </NavLink>
            <NavLink to="/settings" role="menuitem" onClick={close}>
              <MenuIcon name="settings" />
              Settings
            </NavLink>
            {account.role === "admin" && (
              <NavLink to="/admin" role="menuitem" onClick={close}>
                <MenuIcon name="admin" />
                Admin
              </NavLink>
            )}
          </div>
          <div className="nav-profile-div" />
          <div className="nav-profile-nav">
            {account.welcome_url && (
              <a href={account.welcome_url} target="_blank" rel="noreferrer" role="menuitem" onClick={close}>
                <MenuIcon name="tour" />
                Welcome page
              </a>
            )}
            <a href="/docs/user" target="_blank" rel="noreferrer" role="menuitem" onClick={close}>
              <MenuIcon name="docs" />
              Docs
            </a>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                setAbout(true);
              }}
            >
              <MenuIcon name="about" />
              About
            </button>
          </div>
          <div className="nav-profile-div" />
          <button type="button" role="menuitem" className="nav-logout" onClick={logout}>
            <MenuIcon name="logout" />
            Log out
          </button>
        </div>
      )}
      {about && <AboutDialog version={account.version} onClose={() => setAbout(false)} />}
    </div>
  );
};

// What is this thing, which version, and where does it live. Small, but it is
// the first thing anyone asks when reporting a problem, and "check the deb
// version on the box" is not an answer for somebody who only has the web app.
const AboutDialog = ({ version, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
  <div className="modal-backdrop" role="dialog" aria-modal="true" onMouseDown={onClose}>
    <div className="card modal about" onMouseDown={(e) => e.stopPropagation()}>
      <button type="button" className="modal-x" onClick={onClose} title="Close" aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M6 6l12 12M18 6 6 18" />
        </svg>
      </button>
      <div className="about-mark">
        <Wordmark big />
      </div>
      <p className="about-tagline">Self-hosted mini-apps, each with its own container.</p>
      <dl className="about-facts">
        <dt>Version</dt>
        <dd className="about-version">{version || "unknown"}</dd>
        <dt>Author</dt>
        <dd>Philipp C. Heckel</dd>
        <dt>Website</dt>
        <dd>
          <a href="https://heckel.io/hostit" target="_blank" rel="noreferrer">heckel.io/hostit</a>
        </dd>
      </dl>
      {/* No second "Close": the corner X already is one, and two controls with
          the same accessible name in one dialog is what a screen reader cannot
          tell apart. The backdrop closes it too. */}
      <div className="btn-row about-actions">
        <a className="btn" href="/docs/user" target="_blank" rel="noreferrer">Documentation</a>
      </div>
    </div>
  </div>
  );
};

// GlobalToast shows a message any page raises through the hostit:toast event
// (a grant that shared a credential with collaborators, say), so surfaces
// without a toast of their own can still say something.
const GlobalToast = () => {
  const [toast, setToast] = useState(null);
  useEffect(() => {
    let timer = null;
    const onToast = (e) => {
      setToast(e.detail);
      clearTimeout(timer);
      timer = setTimeout(() => setToast(null), 5000);
    };
    window.addEventListener(TOAST_EVENT, onToast);
    return () => {
      window.removeEventListener(TOAST_EVENT, onToast);
      clearTimeout(timer);
    };
  }, []);
  if (!toast) return null;
  return (
    <div className={"snackbar" + (toast.warn ? " snackbar-warn" : "")} role="status" aria-live="polite">
      {toast.warn && <WarnIcon />} {toast.message}
    </div>
  );
};

const App = () => {
  const [account, setAccount] = useState(undefined); // undefined = loading, null = not logged in
  const [error, setError] = useState("");
  const [appHeader, setAppHeader] = useState(null); // the app page's identity, for the mobile nav
  // ?welcome=1 forces the first-run modal for testing, without resetting the
  // onboarded flag in the backend. Dismissing it just closes the overlay.
  const [forceWelcome, setForceWelcome] = useState(() => new URLSearchParams(window.location.search).get("welcome") === "1");
  // The tour opens once, for an account not yet onboarded, and stays open until
  // it is done: its first step marks the account onboarded, and the account is
  // refreshed on every tab switch (a consent popup is one), so gating the tour
  // on the live flag closed it mid-way.
  const [tourOpen, setTourOpen] = useState(false);
  const tourDecided = useRef(false);

  // The docs describe the instance, not the account, so they open without one:
  // they are a link people share, and a tab they leave open next to the app
  const docsOnly = window.location.pathname === "/docs" || window.location.pathname.startsWith("/docs/");
  // The popped-out terminal is its own bare, dark window: no nav, no account gate
  // (the WebSocket carries the same cookie). Rendering it before the gate keeps the
  // app chrome from flashing on a white page while the account and xterm chunk load.
  const termPopout = window.location.pathname.match(/^\/app\/([^/]+)\/terminal\/popout\/?$/);

  // The app rendered, so whatever the last chunk failure was is behind us: let
  // the NEXT deploy have its own single retry rather than inheriting a spent one.
  useEffect(clearChunkReload, []);

  const loadedOnce = useRef(false); // have we ever loaded the account?

  const refreshAccount = useCallback(async () => {
    try {
      setAccount(await api.get("/api/account"));
      loadedOnce.current = true;
      setError("");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setAccount(null);
      } else if (isNetworkError(err) && loadedOnce.current) {
        // A transient blip (e.g. the laptop just woke) after we've loaded once:
        // keep the current view rather than dropping to the failure screen. It
        // heals on the next poll or on reconnect.
      } else {
        setError(err.message);
        setAccount(undefined);
      }
    }
  }, []);

  useEffect(() => {
    if (!docsOnly && !termPopout) {
      refreshAccount();
    }
  }, [refreshAccount, docsOnly, termPopout]);

  // Recover when connectivity returns or the tab is shown again.
  useReconnect(refreshAccount);
  useEffect(() => {
    if (!account || tourDecided.current) return;
    tourDecided.current = true;
    setTourOpen(!account.onboarded);
  }, [account]);
  // Keep full-height views above the mobile keyboard (see --app-vh).
  useAppViewportHeight();

  if (termPopout) {
    // A dark, full-window fallback (same class the terminal itself uses) so the
    // first paint is already the terminal's background, not a white page.
    return (
      <Suspense fallback={<div className="term-page" />}>
        <AppTerminal name={decodeURIComponent(termPopout[1])} fullPage />
      </Suspense>
    );
  }
  if (docsOnly) {
    return (
      <main className="container docs-container">
        <Docs />
      </main>
    );
  }
  if (error) {
    return <LoadFailed message={error} onRetry={refreshAccount} />;
  }
  if (account === undefined) {
    return (
      <div className="center-page">
        <Loading />
      </div>
    );
  }
  if (account === null) {
    return <Login />;
  }
  if (account.status === "pending") {
    return <Pending account={account} />;
  }
  if (account.status === "denied") {
    return <Denied account={account} />;
  }
  return (
    <BrowserRouter>
      <AppHeaderContext.Provider value={setAppHeader}>
        <Nav account={account} appHeader={appHeader} />
        {(tourOpen || forceWelcome) && (
          <WelcomeModal
            account={account}
            refreshAccount={refreshAccount}
            onDone={() => {
              setTourOpen(false);
              setForceWelcome(false);
            }}
          />
        )}
        <GlobalToast />
        <RoutedMain>
          <Routes>
            <Route path="/" element={account.role === "viewer" ? <SharedApps /> : <Dashboard account={account} refreshAccount={refreshAccount} />} />
            <Route path="/chats" element={<Chats account={account} refreshAccount={refreshAccount} />} />
            <Route path="/chats/:name" element={<Chats account={account} refreshAccount={refreshAccount} />} />
            <Route path="/app/:name" element={<AppDetail account={account} refreshAccount={refreshAccount} />} />
            <Route path="/app/:name/:viewSlug" element={<AppDetail account={account} refreshAccount={refreshAccount} />} />
            <Route path="/settings" element={<Settings account={account} refreshAccount={refreshAccount} />} />
            <Route path="/profile" element={<Navigate to="/settings" replace />} />
            <Route path="/connections" element={<Connections />} />
            <Route path="/alerts" element={<Alerts account={account} />} />

            <Route path="/explore" element={<Explore />} />
            <Route path="/admin" element={<Admin account={account} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </RoutedMain>
      </AppHeaderContext.Provider>
    </BrowserRouter>
  );
};

export default App;
